/**
 * PeopleYouMayKnow — safe friend discovery (mutual friends of your friends).
 * Renders as a horizontal "rail" (dashboard / header dropdown) or a "list"
 * (Friends page). Clicking a face opens their profile; Add sends a request.
 */
import React, { useState, useEffect, useCallback } from "react";
import { UserPlus, Sparkles, Check } from "lucide-react";
import axios from "axios";
import { getStickerEmoji } from "./StickerPicker";
import FriendFace from "./FriendFace";
import { useFriendConfig } from "../hooks/useFriendConfig";

const API = process.env.REACT_APP_BACKEND_URL;
const glyph = (c) => getStickerEmoji(c?.sticker) || (c?.display_name || c?.username || "?")[0]?.toUpperCase() || "?";

const openProfile = (id) => window.dispatchEvent(new CustomEvent("iw:open-friend-profile", { detail: { targetId: id } }));

export const PeopleYouMayKnow = ({ uid, fromUsername, variant = "list", title = "People you may know" }) => {
  const { config } = useFriendConfig();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [sent, setSent] = useState({});

  const load = useCallback(async () => {
    if (!uid) return;
    try {
      const r = await axios.get(`${API}/api/friends/${uid}/suggestions`);
      setItems(r.data.suggestions || []);
    } catch { setItems([]); }
    setLoaded(true);
  }, [uid]);

  useEffect(() => { load(); }, [load]);

  const add = async (c) => {
    setSent((s) => ({ ...s, [c.id]: true }));
    try {
      await axios.post(`${API}/api/friends/request`, {
        from_user_id: uid, from_username: fromUsername || "",
        to_user_id: c.id, to_username: c.username || c.display_name,
      });
    } catch { setSent((s) => ({ ...s, [c.id]: false })); }
  };

  if (!loaded || items.length === 0) return null;
  if (!config.suggestions_enabled) return null;

  if (variant === "rail") {
    return (
      <div className="pt-1" data-testid="pymk-rail">
        <p className="text-white/50 text-[10px] font-bold uppercase tracking-wider mb-2 flex items-center gap-1">
          <Sparkles size={11} className="text-amber-400" /> {title}
        </p>
        <div className="flex items-stretch gap-3 overflow-x-auto pb-1" style={{ scrollbarWidth: "thin" }} data-testid="pymk-rail-scroller">
          {items.map((c) => (
            <div key={c.id} className="flex flex-col items-center gap-1 w-[64px] shrink-0" data-testid={`pymk-${c.id}`}>
              <button onClick={() => openProfile(c.id)} className="group" title={`${c.display_name || c.username} · ${c.mutual_count} mutual`}>
                <FriendFace friend={c} className="w-12 h-12 rounded-full flex items-center justify-center text-2xl border-2 border-white/15 group-hover:border-amber-400/60 transition-all" style={{ background: "linear-gradient(135deg, #f59e0b, #8B5CF6)" }} />
              </button>
              <span className="text-white/70 text-[10px] font-bold text-center truncate w-full">{c.display_name || c.username}</span>
              <span className="text-amber-300/60 text-[8px] leading-none">{c.mutual_count} mutual</span>
              <button
                onClick={() => add(c)} disabled={sent[c.id]}
                className="mt-0.5 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-[9px] font-bold hover:bg-emerald-500/25 disabled:opacity-50 flex items-center gap-0.5"
                data-testid={`pymk-add-${c.id}`}
              >
                {sent[c.id] ? <Check size={9} /> : <UserPlus size={9} />} {sent[c.id] ? "Sent" : "Add"}
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // list variant (Friends page)
  return (
    <div className="mt-6" data-testid="pymk-list">
      <h3 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5">
        <Sparkles size={13} className="text-amber-400" /> {title}
      </h3>
      <div className="space-y-3">
        {items.map((c) => (
          <div key={c.id} className="universe-card flex items-center gap-4" data-testid={`pymk-${c.id}`}>
            <button onClick={() => openProfile(c.id)} className="flex items-center gap-4 flex-1 min-w-0 text-left" data-testid={`pymk-open-${c.id}`}>
              <div className="relative">
                <FriendFace friend={c} className="w-12 h-12 rounded-xl flex items-center justify-center text-xl font-bold text-white" style={{ background: "linear-gradient(135deg, #f59e0b, #8B5CF6)" }} />
                {c.online && <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-slate-900 bg-emerald-400" />}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-bold text-sm truncate">{c.display_name || c.username}</h3>
                {c.username && <p className="text-amber-300/50 text-[11px] truncate">@{c.username}</p>}
                <p className="text-amber-300/70 text-xs mt-0.5">{c.mutual_count} mutual {c.mutual_count === 1 ? "friend" : "friends"}</p>
              </div>
            </button>
            <button
              onClick={() => add(c)} disabled={sent[c.id]}
              className="px-3 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-300 text-xs font-bold hover:bg-emerald-500/25 disabled:opacity-50 transition-all flex items-center gap-1"
              data-testid={`pymk-add-${c.id}`}
            >
              {sent[c.id] ? <><Check size={13} /> Sent</> : <><UserPlus size={13} /> Add</>}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PeopleYouMayKnow;
